import {
  ArticleReferences,
  LOCALE_LABELS,
  LOCALES,
  Markdown,
  RADIUS,
} from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { GalleryField } from "~/components/articles/GalleryField";
import { TopicPicker } from "~/components/articles/TopicPicker";
import { VideoPreview } from "~/components/articles/VideoPreview";
import { CoverField } from "~/components/covers/CoverField";
import type { CoverPublishing } from "~/components/covers/use-cover-publishing";
import {
  formatCommaSeparated,
  formatLineSeparated,
  parseCommaSeparated,
  parseLineSeparated,
} from "~/lib/article-metadata";
import { fromDateTimeLocal, toDateTimeLocal } from "~/lib/datetime";
import { slugify } from "~/lib/slug";
import type { CoverContext } from "~/types/covers-admin";
import type {
  ContentFormat,
  ContentTopic,
  NewsArticleAdminDetail,
  NewsArticleCreate,
} from "~/types/news-admin";
import { CONTENT_FORMATS, MIN_CONTENT_TOPICS } from "~/types/news-admin";

/**
 * The article editor: fields on the left, a live preview on the right rendered with
 * the member app's own Markdown component — what the editor sees is what the member
 * gets, because it is literally the same renderer.
 */

// Mirrors the API's slug validation so the round-trip cannot end in a 422.
const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
// A YouTube id is exactly eleven characters from a fixed alphabet. The API
// enforces the same shape; catching it here explains the problem in the form.
const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

interface ArticleFormProps {
  /** The persisted row when editing; absent when creating. */
  initial?: NewsArticleAdminDetail;
  busy: boolean;
  /** An i18n key describing why the last submit failed, or null. */
  errorKey: string | null;
  submitLabel: string;
  submitTestId: string;
  onSubmit: (article: NewsArticleCreate) => void;
  /** Extra actions rendered next to the submit button (e.g. delete). */
  actions?: ReactNode;
  /** Uploads and generates covers from the form's unsaved fields. */
  covers?: CoverPublishing;
  /**
   * Publish one gallery photo and resolve with its URL (null on failure, with the
   * error already surfaced through `errorKey`).
   */
  onUploadGalleryImage?: (
    file: File,
    options: { watermark: boolean },
    context: CoverContext,
  ) => Promise<string | null>;
}

export function ArticleForm({
  initial,
  busy,
  errorKey,
  submitLabel,
  submitTestId,
  onSubmit,
  actions,
  covers,
  onUploadGalleryImage,
}: ArticleFormProps) {
  const { t } = useTranslation();

  const [locale, setLocale] = useState(initial?.locale ?? "cs");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  // Once the slug has been edited by hand (or came from the server), typing in the
  // title must stop rewriting it — renaming an article must not silently move it.
  const [slugTouched, setSlugTouched] = useState(initial !== undefined);
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [topics, setTopics] = useState<ContentTopic[]>(initial?.topics ?? []);
  const [keywords, setKeywords] = useState(
    formatCommaSeparated(initial?.keywords ?? []),
  );
  const [references, setReferences] = useState(
    formatLineSeparated(initial?.references ?? []),
  );
  const [coverImageUrl, setCoverImageUrl] = useState(
    initial?.cover_image_url ?? "",
  );
  const [galleryImageUrls, setGalleryImageUrls] = useState<string[]>(
    initial?.gallery_image_urls ?? [],
  );
  const [publishedAt, setPublishedAt] = useState(
    toDateTimeLocal(initial?.published_at ?? new Date().toISOString()),
  );
  const [isPublished, setIsPublished] = useState(initial?.is_published ?? true);
  const [format, setFormat] = useState<ContentFormat>(
    initial?.format ?? "clanek",
  );
  const [videoId, setVideoId] = useState(initial?.video_youtube_id ?? "");
  // Only raised once a submit has been attempted: the picker starts empty on a new
  // article, and opening the editor already in an error state is noise, not help.
  const [submitted, setSubmitted] = useState(false);

  const slugInvalid = slug.length > 0 && !SLUG_PATTERN.test(slug);
  // Eleven characters from a fixed alphabet. Catching a pasted watch URL here is
  // what keeps a value the player cannot use out of the article.
  const videoInvalid = videoId.length > 0 && !YOUTUBE_ID_PATTERN.test(videoId);
  const topicsMissing = topics.length < MIN_CONTENT_TOPICS;
  const keywordValues = parseCommaSeparated(keywords);
  const referenceValues = parseLineSeparated(references);
  // What a cover published from here is for — read at the moment of the upload, so a
  // draft gets one before it has ever been saved.
  const coverContext: CoverContext = {
    slug,
    title,
    excerpt,
    topics,
    keywords: keywordValues,
  };

  const changeTitle = (next: string) => {
    setTitle(next);
    if (!slugTouched) setSlug(slugify(next));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (slugInvalid || videoInvalid || topicsMissing || slug.length === 0)
      return;
    onSubmit({
      slug,
      locale,
      title: title.trim(),
      excerpt: excerpt.trim() || null,
      body,
      topics,
      keywords: keywordValues,
      references: referenceValues,
      cover_image_url: coverImageUrl.trim() || null,
      gallery_image_urls: galleryImageUrls,
      format,
      video_youtube_id: videoId.trim() || null,
      video_duration_seconds: null,
      published_at: fromDateTimeLocal(publishedAt),
      is_published: isPublished,
    });
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 5fr)" },
        gap: { xs: 4, md: 6 },
        alignItems: "start",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          select
          label={t("editor.fields.locale")}
          value={locale}
          onChange={(event) => setLocale(event.target.value)}
          slotProps={{ htmlInput: { "data-testid": "editor.locale" } }}
          sx={{ maxWidth: 240 }}
        >
          {LOCALES.map((option) => (
            <MenuItem key={option} value={option}>
              {LOCALE_LABELS[option]}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={t("editor.fields.format")}
          value={format}
          onChange={(event) => setFormat(event.target.value as ContentFormat)}
          helperText={t("editor.fields.formatHelp")}
          slotProps={{ htmlInput: { "data-testid": "editor.format" } }}
          sx={{ maxWidth: 240 }}
        >
          {CONTENT_FORMATS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(`formats.${option}`)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={t("editor.fields.videoId")}
          value={videoId}
          onChange={(event) => setVideoId(event.target.value)}
          error={videoInvalid}
          helperText={
            videoInvalid
              ? t("editor.fields.videoIdInvalid")
              : t("editor.fields.videoIdHelp")
          }
          slotProps={{ htmlInput: { "data-testid": "editor.video-id" } }}
          sx={{ maxWidth: 360 }}
        />

        <TextField
          label={t("editor.fields.title")}
          value={title}
          onChange={(event) => changeTitle(event.target.value)}
          required
          fullWidth
          slotProps={{
            htmlInput: { "data-testid": "editor.title", maxLength: 255 },
          }}
        />

        <TextField
          label={t("editor.fields.slug")}
          value={slug}
          onChange={(event) => {
            setSlugTouched(true);
            setSlug(event.target.value);
          }}
          required
          fullWidth
          error={slugInvalid}
          helperText={t("editor.fields.slugHelper")}
          slotProps={{
            htmlInput: { "data-testid": "editor.slug", maxLength: 160 },
          }}
        />

        <TextField
          label={t("editor.fields.excerpt")}
          value={excerpt}
          onChange={(event) => setExcerpt(event.target.value)}
          multiline
          minRows={2}
          fullWidth
          helperText={t("editor.fields.excerptHelper")}
          slotProps={{
            htmlInput: { "data-testid": "editor.excerpt", maxLength: 500 },
          }}
        />

        <TopicPicker
          value={topics}
          onChange={setTopics}
          error={submitted && topicsMissing}
        />

        <TextField
          label={t("editor.fields.keywords")}
          value={keywords}
          onChange={(event) => setKeywords(event.target.value)}
          fullWidth
          helperText={t("editor.fields.keywordsHelper")}
          slotProps={{ htmlInput: { "data-testid": "editor.keywords" } }}
        />

        <TextField
          label={t("editor.fields.body")}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          required
          multiline
          minRows={12}
          fullWidth
          helperText={t("editor.fields.bodyHelper")}
          slotProps={{ htmlInput: { "data-testid": "editor.body" } }}
        />

        <TextField
          label={t("editor.fields.references")}
          value={references}
          onChange={(event) => setReferences(event.target.value)}
          multiline
          minRows={4}
          fullWidth
          helperText={t("editor.fields.referencesHelper")}
          slotProps={{ htmlInput: { "data-testid": "editor.references" } }}
        />

        <CoverField
          value={coverImageUrl}
          onChange={setCoverImageUrl}
          label={t("editor.fields.coverImageUrl")}
          helperText={t("editor.fields.coverImageUrlHelper")}
          inputTestId="editor.cover"
          testIdPrefix="editor"
          disabled={busy}
          busy={covers?.busy}
          onUpload={
            covers &&
            ((file, options) => covers.upload(file, options, coverContext))
          }
          onGenerate={
            covers && ((subject) => covers.generate(subject, coverContext))
          }
        />

        <GalleryField
          value={galleryImageUrls}
          onChange={setGalleryImageUrls}
          disabled={busy}
          onUpload={
            onUploadGalleryImage &&
            ((file, options) =>
              onUploadGalleryImage(file, options, coverContext))
          }
        />

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <TextField
            label={t("editor.fields.publishedAt")}
            type="datetime-local"
            value={publishedAt}
            onChange={(event) => setPublishedAt(event.target.value)}
            required
            helperText={t("editor.fields.publishedAtHelper")}
            slotProps={{
              htmlInput: { "data-testid": "editor.published-at" },
              inputLabel: { shrink: true },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isPublished}
                onChange={(event) => setIsPublished(event.target.checked)}
                data-testid="editor.is-published"
              />
            }
            label={t("editor.fields.isPublished")}
          />
        </Box>

        {errorKey ? (
          <Alert severity="error" data-testid="editor.error">
            {t(errorKey)}
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            data-testid={submitTestId}
          >
            {submitLabel}
          </Button>
          {actions}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          position: { md: "sticky" },
          top: { md: 24 },
        }}
      >
        <Box>
          <Typography variant="OV1" sx={{ color: "text.muted" }}>
            {t("editor.preview")}
          </Typography>
          <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
            {t("editor.previewHint")}
          </Typography>
        </Box>

        <Box
          data-testid="editor.preview"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: `${RADIUS.lg}px`,
            padding: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <VideoPreview
            youtubeId={videoId.trim()}
            title={title}
            valid={videoId.length > 0 && !videoInvalid}
          />

          {coverImageUrl.trim() ? (
            <Box
              component="img"
              src={coverImageUrl.trim()}
              alt=""
              sx={{
                width: "100%",
                // The member app shows covers whole at 16:9; so does the preview.
                aspectRatio: "16 / 9",
                objectFit: "cover",
                borderRadius: `${RADIUS.md}px`,
              }}
            />
          ) : null}
          {galleryImageUrls.length > 0 ? (
            // The member app's thumbnail strip, in miniature: what follows the cover.
            <Box
              data-testid="editor.preview-gallery"
              sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}
            >
              {galleryImageUrls.map((url) => (
                <Box
                  key={url}
                  component="img"
                  src={url}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: 72,
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                    borderRadius: `${RADIUS.sm}px`,
                  }}
                />
              ))}
            </Box>
          ) : null}
          <Typography variant="H2" sx={{ color: "text.primary" }}>
            {title}
          </Typography>
          {excerpt.trim() ? (
            <Typography
              component="p"
              variant="H3"
              sx={{ color: "text.secondary" }}
            >
              {excerpt}
            </Typography>
          ) : null}
          {topics.length > 0 ? (
            <Box
              component="ul"
              aria-label={t("editor.fields.topics")}
              sx={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {topics.map((topic) => (
                <Box component="li" key={topic}>
                  <Chip
                    label={t(`topics.${topic}`)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              ))}
            </Box>
          ) : null}
          <Markdown>{body}</Markdown>
          <ArticleReferences
            heading={t("editor.fields.references")}
            references={referenceValues}
          />
        </Box>
      </Box>
    </Box>
  );
}
